/**
 * Типы презентаций (сторителлинг на карте), общие для API и web.
 * Презентация — режим внутри проекта (НЕ отдельный тип проекта): читатель скроллит
 * публичную страницу, карта пролетает между слайдами и рассказывает историю.
 * План и спека интерфейса: docs/presentations/{plan,ui}.md
 */

/** Сохранённый вид камеры слайда. */
export interface SlideCamera {
  center: [number, number];
  zoom: number;
  pitch: number;
  bearing: number;
}

/**
 * Переход камеры К этому слайду (от предыдущего).
 * v2 добавит keyframes для таймлайна — плеер обязан игнорировать незнакомые поля.
 */
export interface SlideTransition {
  kind: 'fly' | 'ease' | 'jump';
  /** Длительность, мс (default 2000). Не применяется при kind='jump'. */
  durationMs?: number;
  /** Крутизна дуги flyTo (MapLibre curve), только для kind='fly'. */
  curve?: number;
}

/**
 * Куда открывается карточка объекта на слайде: попап у точки или панель справа.
 * Панель — 1 слот (UI держит не более одной panel-карточки); anchor — сколько угодно.
 */
export type SlideFeatureCardPlacement = 'anchor' | 'panel';

/**
 * Авто-открываемая карточка объекта на слайде (docs/presentations/plan.md §4.4).
 * `anchor` — снапшот координат пина при авторинге: позиция попапа устойчива к тайловым
 * слоям (у их фич нет клиентской геометрии) и к тому, что зритель не кликал.
 */
export interface SlideFeatureCard {
  featureId: string;
  layerId: string;
  placement: SlideFeatureCardPlacement;
  anchor?: [number, number];
}

/**
 * Per-object переопределения на слайде. Секции «Слои» в режиме презентации нет —
 * управление видимостью только через клик по объекту на карте (см. ui.md §5.2).
 */
export interface SlideFeatureOverrides {
  /** Объекты, скрытые на этом слайде. */
  hiddenFeatureIds: string[];
  /** Объекты, акцентированные на этом слайде (рендер через feature-state). */
  highlightedFeatureIds: string[];
  /** Авто-открытые карточки объектов на этом слайде (§4.4). */
  cards?: SlideFeatureCard[];
}

/** Позиция контент-блока поверх карты. */
export type ContentBlockPosition = 'left' | 'right' | 'center' | 'bottom' | 'fullscreen';

/**
 * Контент-блок слайда. Унифицирован: все содержательные поля опциональны,
 * «тип» определяется тем, что заполнено (картинка + body = картинка с подписью;
 * только body/title = текстовый блок).
 */
export interface ContentBlock {
  id: string;
  title?: string;
  /** Plain text, переносы строк сохраняются при рендере. */
  body?: string;
  /** URL картинки из существующего upload-пайплайна проекта. */
  imageUrl?: string;
  position: ContentBlockPosition;
  theme?: 'auto' | 'light' | 'dark';
}

/** Поведение и оформление слайда. */
export interface SlideOptions {
  /** Зритель может свободно крутить карту на этом слайде. */
  interactive?: boolean;
  /** Затемнение карты 0–0.6 — читабельность текста поверх. */
  mapDim?: number;
  /** Анимация появления блоков при активации слайда. */
  blockAnimation?: 'none' | 'fade' | 'slide-up';
}

/** Настройки плеера презентации (⚙ в редакторе и share-диалог). */
export interface PresentationSettings {
  /** Стрелки навигации в плеере (default true). */
  showArrows?: boolean;
  /** Индикатор прогресса — точки с активной полоской (default true). */
  showProgress?: boolean;
  /** Глобальная кнопка «исследовать карту» (default false). */
  allowExplore?: boolean;
}

export interface PresentationSlide {
  id: string;
  presentationId: string;
  sortOrder: number;
  name: string;
  camera: SlideCamera;
  featureOverrides: SlideFeatureOverrides | null;
  blocks: ContentBlock[];
  transition: SlideTransition;
  options: SlideOptions;
  createdAt: string;
  updatedAt: string;
}

/**
 * Презентация проекта. 1:1 с проектом (пока), шаринг независим от публичной карты:
 * свой shareToken → публичная страница /p/:token.
 */
export interface Presentation {
  id: string;
  projectId: string;
  isPublic: boolean;
  shareToken: string | null;
  settings: PresentationSettings;
  createdAt: string;
  updatedAt: string;
}

/** Ответ GET/POST /projects/:id/presentation — презентация вместе со слайдами по порядку. */
export interface PresentationWithSlides extends Presentation {
  slides: PresentationSlide[];
}

/* ------------------------------------------------------------------ */
/*  Request payloads                                                   */
/* ------------------------------------------------------------------ */

export interface UpdatePresentationRequest {
  /** Публикация: первое включение генерирует shareToken (токен стабилен дальше). */
  isPublic?: boolean;
  settings?: Partial<PresentationSettings>;
}

export interface CreateSlideRequest {
  camera: SlideCamera;
  name?: string;
  /** Вставить после этого слайда; не задан/не найден — в конец. */
  insertAfterSlideId?: string;
}

/** Частичный autosave слайда — все поля опциональны. */
export interface UpdateSlideRequest {
  name?: string;
  camera?: SlideCamera;
  featureOverrides?: SlideFeatureOverrides | null;
  blocks?: ContentBlock[];
  transition?: SlideTransition;
  options?: SlideOptions;
}

export interface ReorderSlidesRequest {
  /** Полный набор id слайдов презентации в новом порядке (ровно по одному разу). */
  orderedSlideIds: string[];
}

/* ------------------------------------------------------------------ */
/*  Defaults                                                           */
/* ------------------------------------------------------------------ */

export const DEFAULT_SLIDE_TRANSITION: SlideTransition = { kind: 'fly', durationMs: 2000 };

export const DEFAULT_PRESENTATION_SETTINGS: Required<PresentationSettings> = {
  showArrows: true,
  showProgress: true,
  allowExplore: false,
};
